import React, { useEffect, useRef, useState, useCallback } from "react";
import RBImage from "react-bootstrap/Image";
import Modal from "react-bootstrap/Modal"; // ✅ Fix: Use default import
import { useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import picture from "./pictureedit.module.css";

import editicon from "../../../../assets/images/editicon.png";

import Loader from "../../../Utility/Loader";
import ImageCrop from "../../../cropimage/ImageCrop";
import { useCookies } from "../../../../hooks/useCookies";

import { showAlert } from "../../../../utils/alertUtils";
import { useAlert } from "../../../../Context/AlertModalContext";
import { getProfileSelfieImageMeta } from "../../../../utils/serverUtils";

import * as profileAction from "../../../../actions/Profile";
import ConfirmationModal from "../../../Utility/ConfirmationModal";
import { useSocket } from "../../../../Context/SockerContext";

const EditPicture = () => {
  const alert = useAlert();
  const navigate = useNavigate();
  const { getCookie } = useCookies();
  const approvalstatus = getCookie("approval");

  const [loading, setLoading] = useState(false);

  const [imageHashImage, setImageHashImage] = useState("");
  const imageHashMap = useRef({
    main: null,
    first: null,
    second: null,
  });

  const [duplicateImageError, setDuplicateImageError] = useState(false);

  const selectedFileHashesRef = useRef({});
  const fileInputRefs = {
    main: useRef(null),
    first: useRef(null),
    second: useRef(null),
  };

  const [images, setImages] = useState({
    main: null,
    first: null,
    second: null,
  });

  const [imagesupdate, setImagesUpdate] = useState({
    main: null,
    first: null,
    second: null,
  });

  const [type, setType] = useState(32);
  const [mediaid, setMediaId] = useState(null);

  const cropperRef = useRef(null);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [currentImageKey, setCurrentImageKey] = useState(null);
  const [imageUploadSubmit, setImageUploadSubmit] = useState(false);
  const hasFetchedRef = useRef(false);
  const { socket } = useSocket();
  const userId = getCookie("userId");

  const IMAGE_CROP_DIMENSIONS = {
    main: { width: 500, height: 900 },
    first: { width: 600, height: 600 },
    second: { width: 600, height: 600 },
  };
  const { width, height } = IMAGE_CROP_DIMENSIONS[currentImageKey] || { width: 600, height: 600 };

  const [modalConfig, setModalConfig] = useState({
    show: false,
    title: "",
    message: "",
    confirmLabel: "",
    onConfirm: () => { },
  });

  const computeHash = async file => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  };

  const GetUserProfileMedia = useCallback(async () => {
    setLoading(true);
    try {
      const response = await profileAction.getUserMedia();

      if (response) {
        const profileImages = getProfileSelfieImageMeta(response);
        setImages(profileImages);
      }
    } catch (error) {
      console.error("Error fetching profile media:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const GetUserProfileUpdateMedia = useCallback(async () => {
    try {
      const response = await profileAction.getUserUpdateMedia();
      if (response) {
        const profileImages = getProfileSelfieImageMeta(response);
        setImagesUpdate(profileImages);
      }
    } catch (error) {
      console.error("Error fetching updated profile media:", error);
    }
  }, []);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    GetUserProfileMedia();
    GetUserProfileUpdateMedia();
  }, [GetUserProfileMedia, GetUserProfileUpdateMedia]);

  const handleClick = imageKey => {
    setImageHashImage("");
    const input = fileInputRefs[imageKey]?.current;
    if (input) {
      input.value = ""; // <-- Clear the previous file
      input.click();
    }

    const mediaId = images[imageKey]?.id || null;
    const type = imageKey === "main" ? 31 : 32;
    setMediaId(mediaId);
    setType(type);
  };

  const handleFileChange = async (e, imageKey) => {
    const files = e.target?.files;
    if (!files?.length) return;

    const file = files[0];

    // Check for duplicate image by hash
    const fileHash = await computeHash(file);
    const isDuplicate = Object.entries(imageHashMap.current).some(
      ([key, hash]) => hash === fileHash
    );

    if (isDuplicate) {
      setDuplicateImageError(true);
      return;
    }

    setDuplicateImageError(false);
    setImageHashImage(fileHash);
    selectedFileHashesRef.current[imageKey] = fileHash;

    // Read image as base64 for cropping
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result);
    };
    reader.readAsDataURL(file);

    setCurrentImageKey(imageKey);
    setShowCropModal(true);
  };

  const handleCropCancel = useCallback(() => {
    selectedFileHashesRef.current[currentImageKey] = null;

    setDuplicateImageError(false);
    setImageUploadSubmit(false);
    setCurrentImageKey(null);
    setShowCropModal(false);
    setImageToCrop(null);
  }, [currentImageKey]);

  const handleCropSave = async () => {
    try {
      if (!cropperRef.current)
        throw new Error("No cropper reference available");
      const croppedBlob = await cropperRef.current.handleSubmit();
      if (!croppedBlob) throw new Error("Cropping failed");

      const croppedFile = new File([croppedBlob], "cropped-image.jpg", {
        type: "image/jpeg",
      });

      const fileURL = URL.createObjectURL(croppedFile);
      setImagesUpdate(prev => ({
        ...prev,
        [currentImageKey]: {
          ...prev[currentImageKey],
          src: fileURL,
          file: croppedFile,
          fileName: croppedFile.name,
          loading: -1,
        },
      }));
      setImageUploadSubmit(true);
      imageHashMap.current[currentImageKey] = imageHashImage;
    } catch (error) {
      console.error("Error saving images:", error);
    }
  };

  const runImageUpdateFlow = useCallback(async () => {
    const formData = new FormData();
    const fileReady = imagesupdate[currentImageKey]?.file || "";
    if (!fileReady) return;

    if (currentImageKey) formData.append(currentImageKey, fileReady);
    if (mediaid) formData.append("media_id", mediaid);
    if (type) formData.append("type", type);

    try {
      setLoading(true);
      let response = null;

      if (currentImageKey && approvalstatus === "APPROVED") {
        response = await profileAction.updateApproveUserMedia(formData);
      } else {
        response = await profileAction.updateUserMedia(formData);
      }

      const status = response?.status ?? false;
      showAlert(status ? "Success" : "Warning", response?.message, () => {
        if (approvalstatus !== "APPROVED" && status) {
          window.dispatchEvent(
            new CustomEvent("imageUpdate", { detail: response })
          );
          navigate("/user/home");
        }
      });
      handleCropCancel(); // ✅ Used here
    } catch (error) {
      console.error("Error updating user media:", error);
    } finally {
      setLoading(false);
    }
  }, [
    currentImageKey,
    imagesupdate,
    mediaid,
    type,
    approvalstatus,
    navigate,
    handleCropCancel, // ✅ Add to dependency array
  ]);

  useEffect(() => {
    if (!currentImageKey) return;
    if (!imageUploadSubmit) return;

    runImageUpdateFlow();
  }, [imageUploadSubmit, currentImageKey, runImageUpdateFlow]);

  const handleDeleteImages = () => {
    setModalConfig({
      show: true,
      onHide: () => setModalConfig(prev => ({ ...prev, show: false })),
      title: "Alert",
      message: "Are you sure you want to delete image?",
      confirmLabel: "Delete",
      onConfirm: handleDeleteProfileImage,
    });
  };

  const handleDeleteProfileImage = async () => {
    try {
      setLoading(true);
      await profileAction.deleteUserUpdateMedia();

      // Remove all image keys from imageHashMap
      ["main", "first", "second"].forEach(key => {
        imageHashMap.current[key] = null;
      });

      handleCropCancel();
      setImagesUpdate({ main: null, first: null, second: null });
      navigate("/updateprofile");
    } catch (error) {
      console.error("Failed to delete profile image:", error);
    } finally {
      setModalConfig(prev => ({ ...prev, show: false }));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!socket) return;

    const handleSocketData = data => {
      if (data?.userId === userId && data?.event === "fetch_user_data") {
        GetUserProfileMedia();
        GetUserProfileUpdateMedia();
      }
    };

    socket.on("user_socket_emit", handleSocketData);

    return () => {
      socket.off("user_socket_emit", handleSocketData);
    };
  }, [socket, userId, GetUserProfileMedia, GetUserProfileUpdateMedia]);

  return (
    <div className="container">
      {loading && <Loader />}
      <ConfirmationModal {...modalConfig} />

      <div className="row mb-4">
        {["main", "first", "second"].map(key => (
          <div id={key} key={key} className="col mb-3">
            <div
              className="position-relative border rounded"
              style={{
                paddingTop: "100%",
                backgroundImage: `url(${images[key]?.src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="position-absolute bottom-0 end-0 p-1">
                <RBImage
                  title="Edit"
                  src={editicon}
                  className="img-fluid"
                  onClick={() => handleClick(key)}
                  style={{
                    width: "35px",
                    cursor: "pointer",
                  }}
                />
                <input
                  type="file"
                  ref={fileInputRefs[key]}
                  onChange={e => handleFileChange(e, key)}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {approvalstatus === "APPROVED" && duplicateImageError && (
        <div className="alert alert-danger text-center">
          Please add a photo you haven’t already used.
        </div>
      )}

      {approvalstatus === "APPROVED" &&
        (imagesupdate.main || imagesupdate.first || imagesupdate.second) && (
          <>
            <div
              className={picture.underreview}
              style={{
                marginTop: "40px",
                color: "#4E1173",
                fontSize: "16px",
                fontWeight: "600",
                textAlign: "center",
                marginBottom: "20px",
                padding: "25px",
              }}
            >
              <p>
                Thanks for updating your image! It's now under review by
                myTamilDate. You'll receive an update within 24 hours.
              </p>
            </div>

            <div className="row mb-4 justify-content-center">
              {["main", "first", "second"].map(
                key =>
                  imagesupdate[key] && (
                    <div key={key} className="col-md-4 mb-3">
                      <div
                        className="position-relative border rounded"
                        style={{
                          paddingTop: "100%",
                          backgroundImage: `url(${imagesupdate[key].src})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      ></div>
                    </div>
                  )
              )}
            </div>

            <div className="d-flex justify-content-center align-items-center">
              <button
                className="global-cancel-button"
                onClick={handleDeleteImages}
              >
                Delete
              </button>
            </div>
          </>
        )}

      <Modal
        className="customer-modal-without-header-footer crop-modal"
        centered
        show={showCropModal}
        onHide={handleCropCancel}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop your photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImageCrop
            ref={cropperRef}
            url={imageToCrop}
            cropWidth={width}
            cropHeight={height}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 gap-2">
            <button className="global-cancel-button" onClick={handleCropCancel}>
              Cancel
            </button>
            <button className="global-next-btn" onClick={handleCropSave}>
              Add Photo
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditPicture;
